import {inject, Injectable} from './di';
import {AppInitializeService} from './app-initialize-service';
import {Logger} from './logger';

const WSCloseCode = {
  NORMAL: 1000,
  CONNECT_TIMEOUT: 4408,
  PING_SEND_FAILED: 4410,
  HEARTBEAT_TIMEOUT: 4411,
  SEND_FAILED: 4412,
} as const;

const DEFAULT_OPTIONS = {
  protocols: [] as string[],
  connectTimeoutMs: 10_000,
  baseDelayMs: 500,
  maxDelayMs: 30_000,
  jitterRatio: 0.25,
  heartbeatIntervalMs: 60_000,
  heartbeatTimeoutMs: 30_000,
  dropIfNotOpen: true,
} as const;

export type WSOptions<T = unknown> = {
  url: string | (() => string);
  protocols?: string | string[];

  connectTimeoutMs?: number;

  // Reconnect: exponential backoff + jitter
  baseDelayMs?: number;
  maxDelayMs?: number;
  jitterRatio?: number;

  // App-level heartbeat (JSON ping/pong)
  heartbeatIntervalMs?: number;
  heartbeatTimeoutMs?: number;

  onOpen?: () => void;
  onClose?: (ev: CloseEvent) => void;
  onError?: (ev: Event) => void;
  onMessage?: (data: T, raw: MessageEvent) => boolean | void;

  // Called on every successful open (useful for hello/identification)
  onReconnect?: () => void;

  // Custom ping/pong implementation
  pingMessage?: () => string;
  isPong?: (data: unknown) => boolean;

  // If true - send() silently drops, otherwise throws
  dropIfNotOpen?: boolean;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function defaultIsPong(data: unknown): boolean {
  return isRecord(data) && data.type === 'pong';
}

type WSOptionsInternal<T = unknown> = Required<WSOptions<T>>;

@Injectable()
export class WebSocketClient<T = unknown> implements AppInitializeService {
  private readonly logger = inject(Logger);

  private ws: WebSocket | null = null;
  private manualClose = false;

  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;

  private connectTimeoutTimer: number | null = null;

  private heartbeatTimer: number | null = null;
  private heartbeatDeadlineTimer: number | null = null;
  private lastPongAt = 0;
  private lastPingAt = 0;

  private opts: WSOptionsInternal<T>;

  constructor(opts: WSOptions<T>) {
    this.opts = {
      ...DEFAULT_OPTIONS,
      ...opts,
      onOpen: opts.onOpen ?? (() => {}),
      onClose: opts.onClose ?? (() => {}),
      onError: opts.onError ?? (() => {}),
      onMessage: opts.onMessage ?? (() => {}),
      onReconnect: opts.onReconnect ?? (() => {}),
      pingMessage: opts.pingMessage ?? (() => JSON.stringify({ type: "ping", ts: Date.now() })),
      isPong: opts.isPong ?? defaultIsPong,
    };
  }

  init() {
    window.addEventListener("online", () => {
      if (!this.manualClose && !this.isOpen()) this.connect();
    });

    return true;
  }

  connect() {
    this.manualClose = false;
    this.clearReconnectTimer();

    const url = typeof this.opts.url === "function" ? this.opts.url() : this.opts.url;
    this.logger.log(`[WS] connect -> ${url}`);

    let ws: WebSocket;
    try {
      const p = this.opts.protocols;
      ws = p && (Array.isArray(p) ? p.length : true) ? new WebSocket(url, p) : new WebSocket(url);
    } catch (e) {
      this.logger.error("[WS] ctor failed", e);
      this.scheduleReconnect();
      return;
    }

    this.ws = ws;

    // connect timeout
    this.clearConnectTimeout();
    this.connectTimeoutTimer = window.setTimeout(() => {
      this.logger.warn("[WS] connect timeout -> hard reset");
      this.hardReset(WSCloseCode.CONNECT_TIMEOUT, "connect timeout");
    }, this.opts.connectTimeoutMs);

    ws.onopen = () => {
      this.logger.log("[WS] open");
      this.reconnectAttempt = 0;
      this.clearConnectTimeout();

      this.startHeartbeat();

      // Called on every open (including reconnect)
      this.opts.onReconnect();
      this.opts.onOpen();
    };

    ws.onmessage = (ev) => {
      const handleText = (text: string) => {
        let data: unknown = text;
        try {
          data = JSON.parse(text);
        } catch {
          // Keep data as raw text if JSON parsing fails
        }

        if (this.opts.isPong(data)) {
          this.lastPongAt = Date.now();
          this.logger.debug("[WS] pong");
          return;
        }

        this.opts.onMessage(data as T, ev);
      };

      if (typeof ev.data === "string") {
        handleText(ev.data);
      } else if (ev.data instanceof Blob) {
        ev.data.text().then(handleText).catch((e) => this.logger.warn("[WS] blob.text failed", e));
      } else {
        // ArrayBuffer / other
        try {
          handleText(String(ev.data));
        } catch {
          // Silently ignore conversion errors for unsupported data types
        }
      }
    };

    ws.onerror = (ev) => {
      // Browser WS error events are typically opaque
      this.logger.warn("[WS] error", ev);
      this.opts.onError(ev);
    };

    ws.onclose = (ev) => {
      this.logger.warn(`[WS] close code=${ev.code} reason=${ev.reason} clean=${ev.wasClean}`);
      this.stopHeartbeat();
      this.clearConnectTimeout();
      this.ws = null;

      this.opts.onClose(ev);

      if (!this.manualClose) this.scheduleReconnect(ev);
    };
  }

  close(code = 1000, reason = "manual close") {
    this.manualClose = true;
    this.clearReconnectTimer();
    this.stopHeartbeat();
    this.clearConnectTimeout();

    try {
      this.ws?.close(code, reason);
    } catch {
      // Ignore errors when closing already-closed or invalid socket
    }
    this.ws = null;
  }

  isOpen() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Sends message only if socket is OPEN.
   * By default, silently drops to avoid queueing stale messages on TV.
   */
  send(payload: string | object) {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      if (this.opts.dropIfNotOpen) return;
      throw new Error("WebSocket is not open");
    }

    const msg = typeof payload === "string" ? payload : JSON.stringify(payload);
    try {
      ws.send(msg);
    } catch (e) {
      // For TV, trigger reset for quick recovery
      this.logger.warn("[WS] send failed -> hard reset", e);
      this.hardReset(WSCloseCode.SEND_FAILED, "send failed");
      if (!this.opts.dropIfNotOpen) throw e;
    }
  }

  /**
   * Hard reset to avoid zombie TCP state.
   */
  hardReset(code = 4000, reason = "hard reset") {
    this.logger.warn(`[WS] hardReset code=${code} reason=${reason}`);
    try {
      this.ws?.close(code, reason);
    } catch {
      // Ignore errors when closing already-closed or invalid socket
    }
    this.ws = null;

    this.stopHeartbeat();
    this.clearConnectTimeout();

    if (!this.manualClose) this.scheduleReconnect();
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.lastPongAt = Date.now();

    const ping = () => {
      if (!this.isOpen()) return;

      const pingAt = Date.now();
      this.lastPingAt = pingAt;

      try {
        this.ws!.send(this.opts.pingMessage());
        this.logger.debug("[WS] ping");
      } catch (e) {
        this.logger.warn("[WS] ping send failed -> hard reset", e);
        this.hardReset(WSCloseCode.PING_SEND_FAILED, "ping send failed");
        return;
      }

      // Set deadline for pong response for THIS ping
      if (this.heartbeatDeadlineTimer) window.clearTimeout(this.heartbeatDeadlineTimer);
      this.heartbeatDeadlineTimer = window.setTimeout(() => {
        // Key check: pong must arrive AFTER pingAt
        if (this.lastPongAt < pingAt) {
          this.logger.warn("[WS] heartbeat timeout -> hard reset");
          this.hardReset(WSCloseCode.HEARTBEAT_TIMEOUT, "heartbeat timeout");
        } else {
          // Pong received - all good
          this.logger.debug("[WS] heartbeat ok");
        }
      }, this.opts.heartbeatTimeoutMs);
    };

    this.heartbeatTimer = window.setInterval(ping, this.opts.heartbeatIntervalMs);
    ping(); // First ping immediately
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) window.clearInterval(this.heartbeatTimer);
    if (this.heartbeatDeadlineTimer) window.clearTimeout(this.heartbeatDeadlineTimer);
    this.heartbeatTimer = null;
    this.heartbeatDeadlineTimer = null;
  }

  private scheduleReconnect(ev?: CloseEvent) {
    // Normal close - do not reconnect
    if (ev?.code === WSCloseCode.NORMAL) return;

    // If offline - wait for online event
    if (typeof navigator !== "undefined" && "onLine" in navigator && navigator.onLine === false) {
      this.logger.log("[WS] offline -> wait online");
      return;
    }

    this.reconnectAttempt += 1;

    const base = this.opts.baseDelayMs;
    const max = this.opts.maxDelayMs;

    const exp = Math.min(max, base * Math.pow(2, this.reconnectAttempt - 1));
    const jitter = exp * this.opts.jitterRatio * (Math.random() * 2 - 1);
    const delay = Math.max(0, Math.floor(exp + jitter));

    this.logger.log(`[WS] reconnect in ${delay}ms (attempt ${this.reconnectAttempt})`);

    this.clearReconnectTimer();
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private clearConnectTimeout() {
    if (this.connectTimeoutTimer) window.clearTimeout(this.connectTimeoutTimer);
    this.connectTimeoutTimer = null;
  }
}
